import React, { useEffect, useState } from "react"
import { Button, Spinner } from "reactstrap"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { ROUTES } from "constants/routes"
import OnboardingLayout from "pages/Onboarding/OnboardingLayout"
import {
  getPlanPaymentTokenFromUrl,
  setPlanPaymentToken,
  planPaymentGet,
  planPaymentPost,
  getPlanPaymentApiMessage,
} from "helpers/plan_payment_api_helper"
import { getCurrencyLabel } from "utils/currency"

const PlanPayment = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [payLoading, setPayLoading] = useState(false)
  const [redirecting, setRedirecting] = useState(false)
  const [error, setError] = useState("")
  const [session, setSession] = useState(null)

  const verifyPayment = async paymentId => {
    setPayLoading(true)
    setError("")
    try {
      const res = await planPaymentPost("plan-payment/verify", { paymentId })
      if (res?.status) {
        navigate(ROUTES.LOGIN, {
          replace: true,
          state: {
            message: t(
              "Payment successful. Your subscription is now active. Please sign in."
            ),
          },
        })
      } else {
        setError(getPlanPaymentApiMessage(res) || t("Payment verification failed."))
      }
    } catch {
      setError(t("Payment verification failed."))
    } finally {
      setPayLoading(false)
    }
  }

  const loadSession = async () => {
    setLoading(true)
    setError("")
    try {
      const tokenFromUrl = getPlanPaymentTokenFromUrl()
      if (tokenFromUrl) {
        setPlanPaymentToken(tokenFromUrl)
      }
      const res = await planPaymentGet("plan-payment/session")
      if (!res?.status) {
        setError(getPlanPaymentApiMessage(res) || t("Invalid or expired payment link."))
        return
      }
      setSession(res.data || null)
      if (res.data?.alreadyPaid) {
        navigate(ROUTES.LOGIN, {
          replace: true,
          state: {
            message: t("This subscription has already been paid. Please sign in."),
          },
        })
      }
    } catch {
      setError(t("Invalid or expired payment link."))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    document.title = `${t("Subscription Payment")} | Wulooj`
    loadSession()
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const paymentId = params.get("tap_id") || params.get("paymentId")
    if (paymentId && session && !session.alreadyPaid) {
      verifyPayment(paymentId)
    }
  }, [location.search, session])

  const startTapPayment = async () => {
    setPayLoading(true)
    setError("")
    try {
      const res = await planPaymentPost("plan-payment/tap", {})
      if (!res?.status) {
        setError(getPlanPaymentApiMessage(res) || t("Unable to start payment."))
        return
      }
      const data = res.data || {}
      if (data.paymentUrl) {
        setRedirecting(true)
        window.location.href = data.paymentUrl
        return
      }
      const chargeId = data.session?.id
      if (chargeId) {
        await verifyPayment(String(chargeId))
        return
      }
      setError(t("Unable to start Tap payment. Please try again."))
    } catch {
      setError(t("Unable to start payment."))
    } finally {
      setPayLoading(false)
    }
  }

  const plan = session?.plan
  const tenantName = session?.tenant?.name || ""

  return (
    <OnboardingLayout>
      <div className="onboarding-card">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h4 className="mb-0">{t("Complete your subscription payment")}</h4>
          <span className="tap-badge">
            <i className="bx bx-credit-card" />
            {t("Tap")}
          </span>
        </div>

        {tenantName && (
          <p className="text-muted mb-3">
            {t("Tenant")}: <strong>{tenantName}</strong>
          </p>
        )}

        {loading && (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        )}

        {!loading && plan && (
          <div className="onboarding-payment-summary mb-3">
            <div className="d-flex justify-content-between mb-1">
              <span className="text-muted">{t("Plan")}</span>
              <strong>{plan.name}</strong>
            </div>
            <div className="d-flex justify-content-between fw-bold">
              <span>{t("Total")}</span>
              <span>
                {getCurrencyLabel(plan.currency || "SAR")}{" "}
                {Number(plan.price || 0).toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}

        {(payLoading || redirecting) && (
          <div className="text-center py-3 mb-3">
            <Spinner color="primary" className="me-2" />
            {redirecting
              ? t("Redirecting to Tap secure checkout...")
              : t("Processing payment...")}
          </div>
        )}

        {!loading && session && !session.alreadyPaid && (
          <>
            <p className="text-muted mb-4">
              {t(
                "You will be redirected to Tap's secure payment page to complete your subscription."
              )}
            </p>
            <Button
              color="primary"
              type="button"
              disabled={payLoading || redirecting}
              onClick={startTapPayment}
            >
              {payLoading ? <Spinner size="sm" /> : t("Pay with Tap")}
            </Button>
          </>
        )}
      </div>
    </OnboardingLayout>
  )
}

export default PlanPayment
